import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import {
  type DocumentCurrency,
  formatDocumentMoney,
} from "@/shared/lib/performance-document-currency";
import type { InstallmentDTO } from "../../domain/contracts";
import {
  useCreateDocumentFromParent,
  useQuotationInstallmentPlan,
} from "../api/queries";

const FORM_SCHEMA = z.object({
  quotationInstallmentId: z.string().min(1, "ກະລຸນາເລືອກງວດ"),
});

type FormValues = z.infer<typeof FORM_SCHEMA>;

function formatMoney(value: number, currency: DocumentCurrency) {
  return formatDocumentMoney(value, currency);
}

export function CreateInvoiceFromQuotationDialog({
  open,
  onOpenChange,
  eventId,
  quotationId,
  currency,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  quotationId: string;
  currency: DocumentCurrency;
  onCreated?: (documentId: string) => void;
}) {
  const planQuery = useQuotationInstallmentPlan(
    eventId,
    open ? quotationId : null,
  );
  const createInvoice = useCreateDocumentFromParent(eventId);
  const installments = planQuery.data?.installments ?? [];

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FORM_SCHEMA),
    defaultValues: { quotationInstallmentId: "" },
  });

  const selectedId = methods.watch("quotationInstallmentId");

  useEffect(() => {
    if (!open) return;
    const firstAvailable = installments.find((row) => !row.invoicedInvoiceId);
    methods.reset({
      quotationInstallmentId: firstAvailable?.id ?? "",
    });
  }, [open, installments, methods]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ສ້າງໃບເກັບເງິນຈາກໃບສະເໜີລາຄາ</DialogTitle>
        </DialogHeader>

        {planQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດແຜນງວດ...</p>
        ) : installments.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ກະນົດແຜນງວດໃນໃບສະເໜີລາຄາກ່ອນ
          </p>
        ) : (
          <FormRoot
            methods={methods}
            onSubmit={(values) => {
              createInvoice.mutate(
                {
                  parentDocumentId: quotationId,
                  documentType: "invoice",
                  quotationInstallmentId: values.quotationInstallmentId,
                },
                {
                  onSuccess: (document) => {
                    onCreated?.(document.id);
                    onOpenChange(false);
                  },
                },
              );
            }}
            className="space-y-3"
          >
            <p className="text-muted-foreground text-sm">
              ເລືອກງວດທີ່ຕ້ອງການອອກໃບເກັບເງິນ — ງວດທີ່ສ້າງແລ້ວຈະເລືອກບໍ່ໄດ້
            </p>
            <div className="space-y-2">
              {installments.map((row) => (
                <InstallmentOption
                  key={row.id}
                  row={row}
                  currency={currency}
                  selected={selectedId === row.id}
                  disabled={Boolean(row.invoicedInvoiceId)}
                  onSelect={() =>
                    methods.setValue("quotationInstallmentId", row.id, {
                      shouldValidate: true,
                    })
                  }
                />
              ))}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="submit"
                disabled={
                  createInvoice.isPending ||
                  !selectedId ||
                  Boolean(
                    installments.find(
                      (row) =>
                        row.id === selectedId && row.invoicedInvoiceId,
                    ),
                  )
                }
              >
                {createInvoice.isPending ? "ກຳລັງສ້າງ..." : "ສ້າງໃບເກັບເງິນ"}
              </Button>
            </DialogFooter>
          </FormRoot>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InstallmentOption({
  row,
  currency,
  selected,
  disabled,
  onSelect,
}: {
  row: InstallmentDTO;
  currency: DocumentCurrency;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full items-start justify-between gap-3 rounded-lg border p-3 text-left transition-colors ${
        disabled
          ? "cursor-not-allowed border-border bg-muted/40 opacity-70"
          : selected
            ? "border-primary bg-primary/5"
            : "border-border bg-background hover:bg-muted/30"
      }`}
    >
      <div className="min-w-0">
        <p className="font-medium text-sm">
          ງວດ {row.installmentNo}
          {row.label ? ` — ${row.label}` : ""}
        </p>
        <p className="mt-1 font-semibold text-sm tabular-nums">
          {formatMoney(row.amount, currency)}
        </p>
      </div>
      {disabled ? (
        <Badge variant="secondary">ສ້າງແລ້ວ</Badge>
      ) : selected ? (
        <Badge>ເລືອກ</Badge>
      ) : null}
    </button>
  );
}
